import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  DialogActions,
  Alert,
  Box,
} from "@mui/material";

import SuiButton from "components/SuiButton";

import { useProyeccion } from "./hooks/useProyeccion";
import { useDeudas } from "./hooks/useDeudas";

import { ProyeccionProvider } from "./components/proyeccionContext";
import ProyeccionHeader from "./components/proyeccionHeader";
import ProyeccionTabs from "./components/proyeccionTabs";

import ProcesosApi from "api/procesosApi";

import { formatCurrency } from "utils/currency";

// Componente interno que contiene la lógica principal
const ProyeccionModalContent = ({
  open,
  onClose,
  procesoId,
  disponible,
  onComplete,
  onError,
  setPropuestaId: parentSetPropuestaId,
  tipo_propuesta,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [fechaInicio, setFechaInicio] = useState(dayjs().add(1, "month"));
  const [identificador, setIdentificador] = useState(null);
  const [proyeccionData, setProyeccionData] = useState(null);
  const [proyeccionKey, setProyeccionKey] = useState(0);
  const [localError, setLocalError] = useState(null);
  const [errorPorCuotaInsuficiente, setErrorPorCuotaInsuficiente] = useState(false);
  const aplicarCambiosPendientesRef = useRef(null);
  const { deudasPorClase, loading: loadingDeudas } = useDeudas(procesoId, open, disponible);
  const handleSetPropuestaId = useCallback(
    (id) => {
      parentSetPropuestaId(id);
    },
    [parentSetPropuestaId]
  );
  const {
    loading,
    errorMessage,
    setErrorMessage,
    propuestaId,
    setPropuestaId,
    tieneProyeccion,
    configClases,
    setConfigClases,
    handleConfigChange,
    handleModificarCuota,
    initializeConfig,
    generarProyeccion,
    setTieneProyeccion,
    clasesVinculadas,
  } = useProyeccion(procesoId, handleSetPropuestaId, tipo_propuesta);
  const isLoading = loading || loadingDeudas;

  useEffect(() => {
    if (!open || !procesoId) {
      // Resetear estados si el modal se cierra o no hay procesoId
      setTieneProyeccion(false);
      setProyeccionData(null);
      setConfigClases({});
      setLocalError(null);
      return;
    }

    const loadDataAndInitialize = async () => {
      try {
        const procesoResponse = await ProcesosApi.fetchProcesoById(procesoId);
        setIdentificador(procesoResponse.data?.identificador);
        const propuestasResponse = await ProcesosApi.obtenerPropuestas(procesoId, tipo_propuesta);
        const propuestaActiva = propuestasResponse.data[0];
        if (propuestaActiva?.id) {
          setPropuestaId(propuestaActiva.id);

          // Cargar fecha_inicio de la propuesta existente
          if (propuestaActiva.fecha_inicio) {
            try {
              const fechaInicioPropuesta = dayjs(propuestaActiva.fecha_inicio);
              setFechaInicio(fechaInicioPropuesta);
            } catch (error) {
              console.error("Error al parsear fecha_inicio:", error);
            }
          }

          const proyeccionResponse = await ProcesosApi.obtenerProyeccion(propuestaActiva.id);
          if (proyeccionResponse?.data) {
            setProyeccionData(proyeccionResponse.data);
            setTieneProyeccion(true);
            // La inicialización ahora se basa en deudas y la proyección existente, si la hay.
            if (deudasPorClase && Object.keys(deudasPorClase).length > 0) {
              initializeConfig(deudasPorClase, proyeccionResponse.data);
            }
          }
        } else {
          // Si no hay propuesta activa, simplemente inicializamos la configuración
          // una vez que las deudas estén cargadas.
          if (deudasPorClase && Object.keys(deudasPorClase).length > 0) {
            initializeConfig(deudasPorClase);
          }
        }
      } catch (error) {
        console.error("Error al cargar datos iniciales de la proyección:", error);
        if (onError) {
          onError("Error al cargar los datos de la proyección");
        }
      }
    };

    loadDataAndInitialize();

    // Este efecto depende de `open`, `procesoId`, `deudasPorClase` y la función `initializeConfig`.
    // La dependencia de `deudasPorClase` es necesaria, pero como ya corregimos el hook `useDeudas`,
    // este objeto será estable y solo cambiará una vez al abrir el modal.
  }, [open, procesoId, deudasPorClase, initializeConfig, onError, setPropuestaId]);

  // Limpiar error local cuando cambia la fecha de inicio
  useEffect(() => {
    setLocalError(null);
  }, [fechaInicio]);

  const handleModificarCuotaWrapper = (clase, fecha, nuevaCuota, nuevaTasa, numeroCuota) => {
    if (handleModificarCuota) {
      handleModificarCuota(clase, fecha, nuevaCuota, nuevaTasa, numeroCuota);

      // Nuevo: Actualizar cuotas vinculadas si existen
      if (clasesVinculadas[clase]) {
        clasesVinculadas[clase].forEach((claseVinculada) => {
          handleModificarCuota(claseVinculada, fecha, nuevaCuota, nuevaTasa, numeroCuota);
        });
      }
    }
  };

  // Función para manejar la referencia de aplicar cambios pendientes
  const handleCambiosPendientes = (aplicarFuncion) => {
    aplicarCambiosPendientesRef.current = aplicarFuncion;
  };

  const handleGenerar = async () => {
    console.log("Iniciando handleGenerar...");
    // Limpiar TODOS los errores antes de iniciar (local + hook)
    setLocalError(null);
    setErrorMessage(null);

    try {
      const diaInicio = fechaInicio.date();
      if (diaInicio > 27) {
        setLocalError(
          "No se puede generar la proyección con fechas de día superior a 27. Por favor seleccione un día entre 1 y 27."
        );
        return;
      }
      // NUEVO: Aplicar cambios pendientes antes de generar/actualizar
      console.log("Aplicando cambios pendientes...");
      let configClasesActualizadas = configClases;
      if (aplicarCambiosPendientesRef.current) {
        configClasesActualizadas = await aplicarCambiosPendientesRef.current();
        console.log("Cambios pendientes aplicados:", configClasesActualizadas);
      }
      console.log("Verificando datos de deudas...", {
        deudasPorClase,
        configClases: configClasesActualizadas,
      });
      if (!deudasPorClase || Object.keys(deudasPorClase).length === 0) {
        console.log("Error: No hay deudas para procesar");
        throw new Error("No se encontraron datos para generar la proyección");
      }
      console.log("Preparando para generar proyección...", {
        propuestaId,
        tieneProyeccion,
      });
      let response;
      if (!propuestaId) {
        console.log("Generando proyección...");
        response = await generarProyeccion(deudasPorClase, fechaInicio);
        if (response?.id && setPropuestaId) {
          setPropuestaId(response.id);
        }
      } else {
        console.log("Actualizando proyección existente...");
        const configuracion = {};
        const textosPropuesta = [];
        // Preparar grupos de clases vinculadas
        const clasesAgrupadas = [];
        const clasesProcesadas = new Set();
        // Ordenar y agrupar clases
        const clasesOrdenadas = Object.keys(deudasPorClase).sort((a, b) => {
          const orderMap = {
            PRIMERA: 1,
            SEGUNDA: 2,
            TERCERA: 3,
            CUARTA: 4,
            QUINTA: 5,
          };
          const matchA = a.match(/(PRIMERA|SEGUNDA|TERCERA|CUARTA|QUINTA)/);
          const matchB = b.match(/(PRIMERA|SEGUNDA|TERCERA|CUARTA|QUINTA)/);
          return (matchA ? orderMap[matchA[0]] : 999) - (matchB ? orderMap[matchB[0]] : 999);
        });
        // Agrupar clases vinculadas
        clasesOrdenadas.forEach((clase) => {
          if (!clasesProcesadas.has(clase)) {
            const vinculadas = clasesVinculadas[clase] || [];
            const grupo = [clase, ...vinculadas.filter((c) => !clasesProcesadas.has(c))];
            clasesAgrupadas.push(grupo);
            grupo.forEach((c) => clasesProcesadas.add(c));
          }
        });
        let saldoInicial = 0;
        // Procesar cada grupo
        for (const grupo of clasesAgrupadas) {
          if (grupo.length > 1) {
            // Usar la menor cuota base del grupo
            const cuotaCompartida = Math.min(
              ...grupo.map((clase) => parseFloat(configClasesActualizadas[clase].cuotaBase))
            );
            // Configurar cada clase del grupo
            grupo.forEach((clase) => {
              if (tipo_propuesta == "solicitud") {
                saldoInicial = parseFloat(deudasPorClase[clase].totalDeuda);
              }
              if (tipo_propuesta == "conciliacion") {
                saldoInicial = parseFloat(deudasPorClase[clase].totalConciliado);
              }
              if (tipo_propuesta == "acuerdo") {
                saldoInicial = parseFloat(deudasPorClase[clase].totalAcuerdo);
              }
              configuracion[clase] = {
                cuota_base: cuotaCompartida,
                tasa_base: parseFloat(configClasesActualizadas[clase].tasaBase),
                descripcion: configClasesActualizadas[clase].descripcion || "",
                total_deuda: saldoInicial,
                ultima_cuota: configClasesActualizadas[clase].ultima_cuota,
                ultima_fecha: configClasesActualizadas[clase].ultima_fecha,
                modificaciones: configClasesActualizadas[clase].modificaciones || {},
                modo_calculo: configClasesActualizadas[clase].modo_calculo || "CUOTA_FIJA",
                numero_cuotas: configClasesActualizadas[clase].numero_cuotas,
                vinculada_con: grupo.filter((c) => c !== clase),
              };

              // Textos para clases vinculadas
              textosPropuesta.push(
                `Ofrezco una cuota mensual compartida por valor de ${
                  formatCurrency(cuotaCompartida).formatted
                } para la ${clase} que será cancelada simultáneamente con ${grupo
                  .filter((c) => c !== clase)
                  .join(" y ")}`
              );

              if (configClasesActualizadas[clase].tasaBase > 0) {
                textosPropuesta.push(
                  `Reconozco un ${configClasesActualizadas[clase].tasaBase.toFixed(
                    1
                  )}% de interés futuro para la ${clase}`
                );
              }
            });
          } else {
            // Procesar clase individual
            const clase = grupo[0];
            configuracion[clase] = {
              cuota_base: parseFloat(configClasesActualizadas[clase].cuotaBase),
              tasa_base: parseFloat(configClasesActualizadas[clase].tasaBase),
              descripcion: configClasesActualizadas[clase].descripcion || "",
              total_deuda: saldoInicial,
              ultima_cuota: configClasesActualizadas[clase].ultima_cuota,
              ultima_fecha: configClasesActualizadas[clase].ultima_fecha,
              modificaciones: configClasesActualizadas[clase].modificaciones || {},
              modo_calculo: configClasesActualizadas[clase].modo_calculo,
              numero_cuotas: configClasesActualizadas[clase].numero_cuotas,
            };

            // Textos para clase individual
            if (configuracion[clase].cuota_base > 0) {
              textosPropuesta.push(
                `Ofrezco una cuota mensual por valor de ${
                  formatCurrency(configuracion[clase].cuota_base).formatted
                } para la ${clase} que será cancelada de acuerdo a la prelación de créditos establecida por la ley`
              );
            }

            if (configuracion[clase].tasa_base > 0) {
              textosPropuesta.push(
                `Reconozco un ${configuracion[clase].tasa_base.toFixed(
                  1
                )}% de interés futuro para la ${clase}`
              );
            }
          }
          // Agregar descripción adicional si existe
          const clase = grupo[0];
          if (configClasesActualizadas[clase].descripcion?.trim()) {
            textosPropuesta.push(configClasesActualizadas[clase].descripcion.trim());
          }
        }
        const propuestaData = {
          proceso: procesoId,
          fecha_inicio: fechaInicio.format("YYYY-MM-DD"),
          configuracion,
          clases_vinculadas: clasesVinculadas,
          activa: true,
          descripcion_general: textosPropuesta.join("\n\n"),
        };
        // Actualizar propuesta y obtener nueva proyección
        await ProcesosApi.actualizarPropuesta(propuestaId, propuestaData);
        response = await ProcesosApi.obtenerProyeccion(propuestaId);
        if (response?.data?.proyeccion) {
          setProyeccionData(response.data);
          setProyeccionKey((prev) => prev + 1); // Esto forzará el remount
          setConfigClases((prevConfig) => {
            const newConfig = { ...prevConfig };
            Object.entries(configuracion).forEach(([clase, config]) => {
              newConfig[clase] = {
                ...prevConfig[clase],
                cuotaBase: config.cuota_base,
                cuotaBaseOriginal: config.cuota_base,
                tasaBase: config.tasa_base,
                tasaBaseOriginal: config.tasa_base,
                descripcion: config.descripcion,
                modo_calculo: config.modo_calculo,
                numero_cuotas: config.numero_cuotas,
                modificaciones: {},
                proyeccion: response.data.proyeccion[clase]?.proyeccion || [],
              };
            });
            return newConfig;
          });

          setTieneProyeccion(false);
          setTimeout(() => setTieneProyeccion(true), 0);
        }
      }
      const responseData = response?.data || response; // Manejar ambos casos de respuesta
      if (responseData) {
        console.log("Proyección generada con éxito, procesando datos...");
        try {
          console.log("Calculando totales...");
          // Calcular totalDeuda Y plazoMeses
          let totalDeuda = 0;
          let plazoMeses = 0;
          if (tipo_propuesta == "solicitud") {
            totalDeuda = Object.values(deudasPorClase).reduce(
              (sum, clase) => sum + parseFloat(clase.totalDeuda || 0),
              0
            );
            plazoMeses = Math.max(
              ...Object.values(configClasesActualizadas).map((clase) =>
                Math.ceil(parseFloat(clase.totalDeuda || 0) / parseFloat(clase.cuotaBase || 1))
              )
            );
          }
          if (tipo_propuesta == "conciliacion") {
            totalDeuda = Object.values(deudasPorClase).reduce(
              (sum, clase) => sum + parseFloat(clase.totalConciliado || 0),
              0
            );
            plazoMeses = Math.max(
              ...Object.values(configClasesActualizadas).map((clase) =>
                Math.ceil(parseFloat(clase.totalConciliado || 0) / parseFloat(clase.cuotaBase || 1))
              )
            );
          }
          if (tipo_propuesta == "acuerdo") {
            totalDeuda = Object.values(deudasPorClase).reduce(
              (sum, clase) => sum + parseFloat(clase.totalAcuerdo || 0),
              0
            );
          }
          console.log("Total deuda calculado:", totalDeuda);
          console.log("Plazo meses calculado:", plazoMeses);

          // Calcular cuotaMensual
          const cuotaMensual = Object.values(configClasesActualizadas).reduce(
            (sum, clase) => sum + parseFloat(clase.cuotaBase || 0),
            0
          );
          console.log("Cuota mensual calculada:", cuotaMensual);

          const proyeccionData = {
            totalDeuda,
            cuotaMensual,
            plazoMeses,
            resumen: responseData.proyeccion,
            propuestaId: responseData.id || propuestaId,
            success: true,
            unblockNext: true,
            hasProyeccion: true,
          };
          console.log("Datos de proyección preparados:", proyeccionData);

          if (onComplete) {
            await onComplete({
              ...proyeccionData,
              propuestaId: response.id || propuestaId,
              unblockNext: true,
              success: true,
              hasProyeccion: true,
            });
          }
          // Actualizar estados finales
          setTieneProyeccion(true);
          if (responseData.id && setPropuestaId) {
            setPropuestaId(responseData.id);
          }
          return proyeccionData;
        } catch (innerError) {
          console.error("Error detallado en el procesamiento:", {
            message: innerError.message,
            stack: innerError.stack,
          });
          if (onError) {
            onError("Error procesando datos de la proyección");
          }
          throw innerError;
        }
      } else {
        throw new Error("No se recibió una respuesta válida del servidor");
      }
    } catch (error) {
      console.error("Error detallado en handle Generar.", {
        message: error.message,
        stack: error.stack,
        response: error.response,
      });

      // Capturar error del backend y mostrarlo en el modal
      let mensajeError = "Error al generar la proyección";

      if (error.response?.data?.error) {
        // Error del backend (ej: validación de iteraciones excedidas)
        mensajeError = error.response.data.error;

        // Detectar error de cuota insuficiente
        if (mensajeError.includes("[CUOTA_INSUFICIENTE]")) {
          setErrorPorCuotaInsuficiente(true);
        }
      } else if (error.message) {
        // Error de frontend (ej: validación de fecha)
        mensajeError = error.message;
      }

      setLocalError(mensajeError);

      // NUEVO: Recargar configuración válida desde el backend después del error
      // Esto evita que el estado local mantenga los valores que causaron el error
      if (propuestaId) {
        try {
          console.log("Recargando configuración válida desde el backend...");
          const propuestaResponse = await ProcesosApi.obtenerPropuestas(procesoId, tipo_propuesta);
          const propuestaActiva = propuestaResponse.data[0];

          if (propuestaActiva?.configuracion) {
            const newConfig = {};
            Object.entries(propuestaActiva.configuracion).forEach(([clase, config]) => {
              newConfig[clase] = {
                cuotaBase: parseFloat(config.cuota_base),
                tasaBase: parseFloat(config.tasa_base),
                cuotaBaseOriginal: parseFloat(config.cuota_base),
                tasaBaseOriginal: parseFloat(config.tasa_base),
                descripcion: config.descripcion || "",
                modificaciones: config.modificaciones || {},
                totalDeuda: parseFloat(config.total_deuda),
                ultimaCuota: config.ultima_cuota || 0,
                ultimaFecha: config.ultima_fecha || null,
                modo_calculo: config.modo_calculo,
                numero_cuotas: config.numero_cuotas,
              };
            });

            // Recargar también la proyección existente
            const proyeccionResponse = await ProcesosApi.obtenerProyeccion(propuestaId);
            if (proyeccionResponse?.data?.proyeccion) {
              Object.entries(proyeccionResponse.data.proyeccion).forEach(([clase, data]) => {
                if (newConfig[clase]) {
                  newConfig[clase].proyeccion = data.proyeccion;
                }
              });
            }

            // Restaurar configuración válida
            setConfigClases(newConfig);
            console.log("Configuración válida restaurada:", newConfig);
          }
        } catch (reloadError) {
          console.error("Error al recargar configuración válida:", reloadError);
        }
      }

      // También notificar al padre si es un error crítico
      if (onError && !error.response?.data?.error) {
        onError(mensajeError);
      }

      // NO lanzar el error para que el modal permanezca abierto
      // throw error;
    }
  };

  const handleCloseModal = () => {
    onClose();
  };

  const handleReiniciarProyeccion = async () => {
    try {
      const configuracionBase = {};
      const disponibleNumerico =
        typeof disponible === "string"
          ? parseFloat(
              disponible
                .replace(/\./g, "")
                .replace(",", ".")
                .replace(/[^\d.]/g, "")
            )
          : parseFloat(disponible);
      // Crear configuración base para cada clase
      Object.keys(deudasPorClase).forEach((clase) => {
        const deudas = deudasPorClase[clase].deudas || [];

        // Calcular totalDeuda excluyendo deudas postergadas y COSTAS
        const totalDeuda = deudas
          .filter((d) => !d.postergado && !d.costas)
          .reduce((sum, d) => sum + parseFloat(d.capital || 0), 0);

        // Estructura específica para reinicio (sin cálculos de cuota mínima)
        configuracionBase[clase] = {
          cuota_base: disponibleNumerico, // Usar disponible directamente
          tasa_base: 0, // Siempre 0 en reinicio
          descripcion: "",
          total_deuda: totalDeuda,
          modificaciones: {}, // Importante: debe ser un objeto vacío
          modo_calculo: "CUOTA_FIJA", // Importante: especificar CUOTA_FIJA
          numero_cuotas: 0, // Importante: debe ser 0
          ultima_cuota: 0,
          ultima_fecha: null,
        };
      });
      //alert("configuracionBase: " + JSON.stringify(configuracionBase));
      // Preparar datos para la actualización
      const data = {
        proceso: procesoId,
        fecha_inicio: fechaInicio.format("YYYY-MM-DD"),
        configuracion: configuracionBase,
        clases_vinculadas: {}, // Eliminar todas las vinculaciones
        activa: true,
      };
      // Actualizar la propuesta
      const response = await ProcesosApi.actualizarPropuesta(propuestaId, data);
      if (response.status === 200) {
        // Obtener la nueva proyección
        const proyeccionResponse = await ProcesosApi.obtenerProyeccion(propuestaId);
        // Actualizar estados
        if (proyeccionResponse?.data) {
          setConfigClases(configuracionBase);
          setProyeccionData(proyeccionResponse.data);
          // Forzar re-renderizado de la proyección
          setTieneProyeccion(false);
          setTimeout(() => setTieneProyeccion(true), 0);

          // Limpiar estados de error
          setLocalError(null);
          setErrorMessage(null);
          setErrorPorCuotaInsuficiente(false);

          // Notificar éxito al usuario
          if (onError) {
            onError("Proyección reiniciada exitosamente con tasa de interés en 0%");
          }
        }
      }
    } catch (error) {
      console.error("Error al reiniciar proyección:", error);
      if (onError) {
        onError("Error al reiniciar la proyección");
      }
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "90vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <ProyeccionHeader
            procesoId={procesoId}
            identificador={identificador}
            fechaInicio={fechaInicio}
            setFechaInicio={setFechaInicio}
            onReiniciar={handleReiniciarProyeccion}
            propuestaId={propuestaId}
            tieneProyeccion={tieneProyeccion}
            tipo_propuesta={tipo_propuesta}
          />
          <Divider sx={{ mb: -1 }} />
        </DialogTitle>
        <DialogContent>
          {(localError || errorMessage) && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Alert severity="error">{localError || errorMessage}</Alert>
            </Box>
          )}
          {errorPorCuotaInsuficiente && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Alert severity="info">
                {(localError || errorMessage)?.includes("muy pequeña") ||
                (localError || errorMessage)?.includes("límite de")
                  ? "La cuota configurada es muy pequeña. Use REINICIAR para calcular automáticamente una cuota viable que complete la proyección en un plazo razonable."
                  : "La cuota es insuficiente para cubrir los intereses generados. Use REINICIAR para establecer la tasa en 0% y generar una propuesta sin intereses."}
              </Alert>
            </Box>
          )}
          <ProyeccionTabs
            key={proyeccionKey}
            activeTab={activeTab}
            propuestaId={propuestaId}
            proceso={procesoId}
            setActiveTab={setActiveTab}
            deudasPorClase={deudasPorClase}
            configClases={configClases}
            setConfigClases={setConfigClases}
            proyeccionData={proyeccionData || {}}
            handleConfigChange={handleConfigChange}
            handleModificarCuota={handleModificarCuotaWrapper}
            fechaInicio={fechaInicio}
            tieneProyeccion={tieneProyeccion}
            setTieneProyeccion={setTieneProyeccion}
            setProyeccionData={setProyeccionData}
            disponible={disponible}
            onCambiosPendientes={handleCambiosPendientes} // NUEVA PROP
            tipo_propuesta={tipo_propuesta}
          />
        </DialogContent>
        <DialogActions>
          <SuiButton variant="text" color="secondary" onClick={handleCloseModal}>
            Cancelar
          </SuiButton>
          <SuiButton
            variant="gradient"
            buttonColor="dark"
            onClick={() => {
              console.log("Click en botón Generar/Actualizar");
              handleGenerar();
            }}
            disabled={isLoading}
          >
            {isLoading
              ? "Calculando..."
              : propuestaId
              ? "Actualizar Proyección"
              : "Calcular Proyección"}
          </SuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Componente wrapper que provee el contexto
const ProyeccionModal = ({
  open,
  onClose,
  procesoId,
  disponible,
  onComplete,
  onError,
  tipo_propuesta,
}) => {
  const propuestaIdRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [propuestaId, setPropuestaId] = useState(null);
  // Verificar propuesta (solo una vez)
  useEffect(() => {
    const verificar = async () => {
      if (!procesoId || propuestaIdRef.current) return;
      console.log("Verificando propuesta...");
      try {
        const propuestasResponse = await ProcesosApi.obtenerPropuestas(procesoId, tipo_propuesta);
        const propuestaActiva = propuestasResponse.data[0];
        //setHasPropuesta(!!propuestaActiva);
        if (propuestaActiva?.id) {
          propuestaIdRef.current = propuestaActiva.id;
          setPropuestaId(propuestaActiva.id);
          //const proyeccionResponse = await ProcesosApi.obtenerProyeccion(propuestaActiva.id);
          //const tieneProyeccion = !!proyeccionResponse?.data;
        }
      } catch (error) {
        console.error("Error verificando propuesta:", error);
      }
    };

    verificar();
  }, [procesoId]);

  return (
    <ProyeccionProvider>
      <ProyeccionModalContent
        open={open}
        onClose={onClose}
        procesoId={procesoId}
        disponible={disponible}
        setPropuestaId={setPropuestaId}
        tipo_propuesta={tipo_propuesta}
        onComplete={(proyeccionData) => {
          // Asegurarnos que onComplete reciba los datos necesarios para desbloquear el botón
          if (onComplete) {
            onComplete({
              ...proyeccionData,
              unblockNext: true,
              success: true,
            });
          }
        }}
        onError={onError}
      />
    </ProyeccionProvider>
  );
};

ProyeccionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  procesoId: PropTypes.number.isRequired,
  disponible: PropTypes.number.isRequired,
  onComplete: PropTypes.func,
  onError: PropTypes.func,
  tipo_propuesta: PropTypes.string,
};

ProyeccionModalContent.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  procesoId: PropTypes.number.isRequired,
  disponible: PropTypes.number.isRequired,
  onComplete: PropTypes.func,
  onError: PropTypes.func,
  setPropuestaId: PropTypes.func,
  tipo_propuesta: PropTypes.string,
};

export default ProyeccionModal;
